import { useState } from 'react';
import { useAppointmentHistory, useUpcomingAppointments, useCancelAppointment, useRescheduleAppointment } from '@/hooks';
import type { AppointmentListItem } from '@/api';
import { Button } from '@medikei/ui';
import { Video, MapPin, Home, Plus } from 'lucide-react';
import {
  AppointmentList,
  CancelDialog,
  RescheduleDialog,
  InAppSchedulingModal,
} from '@/components/scheduling';
import { cn } from '@/utils/cn';

export function MyAppointmentsPage() {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'history'>('upcoming');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState<AppointmentListItem | null>(null);

  // Modal de Agendamento Rápido no Dashboard Logado
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false);
  const [schedulingModality, setSchedulingModality] = useState<'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR'>('TELEMEDICINA');

  const { data: upcomingData, isLoading: isLoadingUpcoming, isError: isErrorUpcoming, refetch: refetchUpcoming } = useUpcomingAppointments();
  const { data: historyData, isLoading: isLoadingHistory, isError: isErrorHistory, refetch: refetchHistory } = useAppointmentHistory();
  const cancelAppointment = useCancelAppointment();
  const rescheduleAppointment = useRescheduleAppointment();

  const handleCancel = (appointmentId: string) => {
    setAppointmentToCancel(appointmentId);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async (reason: string) => {
    if (appointmentToCancel) {
      await cancelAppointment.mutateAsync({
        appointmentId: appointmentToCancel,
        reason,
      });
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
    }
  };

  const handleReschedule = (appointmentId: string) => {
    const apt = upcomingData?.appointments.find((a) => a.id === appointmentId);
    if (apt) {
      setAppointmentToReschedule(apt);
      setRescheduleDialogOpen(true);
    }
  };

  const handleConfirmReschedule = async (newSlotId: string) => {
    if (appointmentToReschedule) {
      await rescheduleAppointment.mutateAsync({
        appointmentId: appointmentToReschedule.id,
        newSlotId,
      });
      setRescheduleDialogOpen(false);
      setAppointmentToReschedule(null);
      refetchUpcoming();
    }
  };

  const handleOpenScheduling = (modality: 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR') => {
    setSchedulingModality(modality);
    setIsSchedulingModalOpen(true);
  };

  const handleNewAppointment = () => {
    handleOpenScheduling('TELEMEDICINA');
  };

  const rescheduleDoctorId = appointmentToReschedule?.doctorId || appointmentToReschedule?.doctor?.id;

  return (
    <div className="container max-w-3xl py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight">Meus Agendamentos</h1>
          <p className="text-muted-foreground text-sm mt-1">Gerencie suas consultas e acompanhe seu histórico clínico</p>
        </div>

        {(isLoadingUpcoming || isLoadingHistory) && (
          <div className="flex items-center gap-2 text-xs font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full animate-pulse self-start sm:self-auto">
            <svg className="w-3.5 h-3.5 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span>Carregando agenda...</span>
          </div>
        )}
      </div>

      {/* Banner de Destaque para Marcar Nova Consulta */}
      <div className="mb-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shrink-0 shadow-sm">
            <Plus className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm sm:text-base text-foreground leading-snug">
              Marcar Nova Consulta
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Agende agora no painel escolhendo uma das modalidades:
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="rounded-xl text-xs font-bold gap-1.5 h-9 px-3.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
            onClick={() => handleOpenScheduling('TELEMEDICINA')}
          >
            <Video className="w-3.5 h-3.5" />
            <span>+ Telemedicina</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="rounded-xl text-xs font-bold gap-1.5 h-9 px-3.5 bg-background border-blue-500/40 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 shadow-xs"
            onClick={() => handleOpenScheduling('PRESENCIAL')}
          >
            <MapPin className="w-3.5 h-3.5 text-blue-600" />
            <span>+ Consultório</span>
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="rounded-xl text-xs font-bold gap-1.5 h-9 px-3.5 bg-background border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 shadow-xs"
            onClick={() => handleOpenScheduling('DOMICILIAR')}
          >
            <Home className="w-3.5 h-3.5 text-emerald-600" />
            <span>+ Médico em Casa</span>
          </Button>
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'upcoming'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Próximas consultas
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'history'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Histórico
        </button>
      </div>

      {activeTab === 'upcoming' && (
        <AppointmentList
          appointments={upcomingData?.appointments || []}
          isLoading={isLoadingUpcoming}
          isError={isErrorUpcoming}
          userRole="patient"
          emptyTitle="Nenhuma consulta agendada"
          emptyDescription="Você ainda não tem consultas marcadas. Que tal agendar sua próxima consulta?"
          emptyActionLabel="Ver médicos"
          onEmptyAction={handleNewAppointment}
          onCancel={handleCancel}
          onReschedule={handleReschedule}
          onRetry={() => refetchUpcoming()}
        />
      )}

      {activeTab === 'history' && (
        <AppointmentList
          appointments={historyData?.appointments || []}
          isLoading={isLoadingHistory}
          isError={isErrorHistory}
          userRole="patient"
          emptyTitle="Nenhum histórico de consultas"
          emptyDescription="Suas consultas anteriores aparecerão aqui."
          emptyActionLabel="Ver médicos"
          onEmptyAction={handleNewAppointment}
          onRetry={() => refetchHistory()}
        />
      )}

      <CancelDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setAppointmentToCancel(null);
        }}
        onConfirm={handleConfirmCancel}
        isLoading={cancelAppointment.isPending}
      />

      <InAppSchedulingModal
        isOpen={isSchedulingModalOpen}
        onClose={() => setIsSchedulingModalOpen(false)}
        initialModality={schedulingModality}
        onSuccess={() => {
          refetchUpcoming();
          refetchHistory();
        }}
      />

      {rescheduleDialogOpen && rescheduleDoctorId && appointmentToReschedule && (
        <RescheduleDialog
          isOpen={rescheduleDialogOpen}
          onClose={() => {
            setRescheduleDialogOpen(false);
            setAppointmentToReschedule(null);
          }}
          onConfirm={handleConfirmReschedule}
          doctorId={rescheduleDoctorId}
          currentDate={appointmentToReschedule.scheduledAt}
          isLoading={rescheduleAppointment.isPending}
        />
      )}
    </div>
  );
}
